import { c as _c } from "react/compiler-runtime";
import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { SessionsLayout } from '@src/components/sco/Advisors/layout/SessionsLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { SessionBookForm } from '@src/components/sco/Advisors/sections/SessionBookForm';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { acceptingSessionsDirectoryHref } from '@src/components/sco/Advisors/utils/directoryFilters';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';

/**
 * In-app book flow under Advisors chrome. Public share links use `/members/{slug}/book`.
 * Guests stay here (L3/L12): the same `SessionBookForm`, which asks them to register or log in after
 * the times step. Guests get a plain shell instead of the Sessions rail (its views are member-only).
 */
export const SessionBookSection = () => {
  const $ = _c(29);
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  const isGuest = !currentUser || !!currentUser.isGuest;
  const router = useRouter();
  let t0;
  if ($[0] !== router.isReady || $[1] !== router.query) {
    t0 = router.isReady ? String(router.query.expertUserId || "").trim() : "";
    $[0] = router.isReady;
    $[1] = router.query;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const expertUserId = t0;
  let t1;
  if ($[3] !== router.isReady || $[4] !== router.query) {
    t1 = router.isReady ? String(router.query.waive || "").trim() : "";
    $[3] = router.isReady;
    $[4] = router.query;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const waiveCode = t1;
  let t2;
  if ($[6] !== router.isReady || $[7] !== router.query) {
    t2 = router.isReady ? String(router.query.ref || "").trim() : "";
    $[6] = router.isReady;
    $[7] = router.query;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const refParam = t2;
  let t3;
  if ($[9] !== paths) {
    t3 = acceptingSessionsDirectoryHref(paths);
    $[9] = paths;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const directoryBookHref = t3;
  let t4;
  let t5;
  if ($[11] !== directoryBookHref || $[12] !== expertUserId || $[13] !== router) {
    t4 = () => {
      if (!router.isReady) {
        return;
      }
      if (!expertUserId) {
        router.replace(directoryBookHref);
      }
    };
    t5 = [directoryBookHref, expertUserId, router];
    $[11] = directoryBookHref;
    $[12] = expertUserId;
    $[13] = router;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  const complimentary = !!waiveCode;
  const description = complimentary ? "This complimentary link waives session fees. Propose times that work for you \u2014 no card hold if the coupon is valid." : "Propose times that work for you. We\u2019ll hold the package on your card; you\u2019re charged when the expert confirms a slot.";
  const [guestShell] = useState(isGuest);
  let t6;
  if ($[16] !== description || $[17] !== guestShell) {
    t6 = children => guestShell ? <GuestBookShell description={description}>{children}</GuestBookShell> : <SessionsLayout activeKey="book" description={description} title="Book a session">{children}</SessionsLayout>;
    $[16] = description;
    $[17] = guestShell;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const shell = t6;
  if (!router.isReady || !expertUserId) {
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AdvisorsQueryState loading={true}>{null}</AdvisorsQueryState>;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== shell) {
      t8 = shell(t7);
      $[20] = shell;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  let t7;
  if ($[22] !== expertUserId || $[23] !== refParam || $[24] !== waiveCode) {
    t7 = <SessionBookForm entry="advisors" expertUserId={expertUserId} refParam={refParam} waiveCode={waiveCode} />;
    $[22] = expertUserId;
    $[23] = refParam;
    $[24] = waiveCode;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== shell || $[27] !== t7) {
    t8 = shell(t7);
    $[26] = shell;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
};

/** Guest book page body: no Sessions rail (All / Upcoming / Past are member views). */
const GuestBookShell = t0 => {
  const $ = _c(12);
  const {
    description,
    children
  } = t0;
  const paths = useAdvisorsPaths();
  let t1;
  if ($[0] !== paths.directory || $[1] !== paths.sessionNew) {
    t1 = advisorsBreadcrumbsWithTrail({
      title: "Find advisors",
      href: paths.directory
    }, {
      title: "Book a session",
      href: paths.sessionNew
    });
    $[0] = paths.directory;
    $[1] = paths.sessionNew;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      breadcrumbs: t1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useAdvisorsToolbar(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      base: 4,
      md: 8
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Heading size="2xl">Book a session</Heading>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== description) {
    t5 = <VStack align="stretch" gap={1}>{t4}<Text color="colorPalette.fg/60">{description}</Text></VStack>;
    $[7] = description;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children || $[10] !== t5) {
    t6 = <Box height="full" minHeight={0} overflowY="auto" width="full"><VStack align="stretch" gap={6} marginX="auto" maxWidth="3xl" padding={t3}>{t5}{children}</VStack></Box>;
    $[9] = children;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};